<template>
  <div class="markdown-viewer">
    <Card v-if="loading">
      <template #content>
        <div class="flex justify-center items-center py-8">
          <ProgressSpinner />
        </div>
      </template>
    </Card>
    
    <Card v-else-if="error">
      <template #content>
        <Message severity="error" :closable="false">
          {{ error }}
        </Message>
      </template>
    </Card>
    
    <Card v-else class="markdown-content-card">
      <template #content>
        <div 
          class="markdown-content prose prose-slate max-w-none"
          v-html="renderedContent"
        />
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch} from "vue";
import MarkdownIt from "markdown-it";
import {docsBackend} from "@/backend/docs.backend.ts";
import Card from "primevue/card";
import Message from "primevue/message";
import ProgressSpinner from "primevue/progressspinner";

interface Props {
  filename: string;
}

const props = defineProps<Props>();

const loading = ref(false);
const error = ref<string | null>(null);
const content = ref<string>("");

// Initialize markdown-it with sensible defaults
const md = new MarkdownIt({
  html: false, // Disable HTML tags in source for security
  linkify: true, // Auto-convert URL-like text to links
  typographer: true, // Enable smartquotes and other typographic replacements
  breaks: true, // Convert '\n' in paragraphs into <br>
});

const renderedContent = computed(() => {
  if (!content.value) return "";
  return md.render(content.value);
});

async function loadDocument() {
  loading.value = true;
  error.value = null;
  
  try {
    const doc = await docsBackend.getDocument(props.filename);
    content.value = doc.content;
  } catch (err: any) {
    console.error("Error loading document:", err);
    error.value = err.message || "Failed to load document";
  } finally {
    loading.value = false;
  }
}

// Watch for filename changes
watch(() => props.filename, () => {
  loadDocument();
});

onMounted(() => {
  loadDocument();
});
</script>

<style scoped>
/* Prose styling for markdown content - using Tailwind typography */
.markdown-content {
  line-height: 1.7;
}

/* Override some prose defaults to match PrimeVue theme */

:deep(.markdown-content h1) {
  font-size: 2.25rem;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 1rem;
  border-bottom: 2px solid;
  padding-bottom: 0.5rem;
}

:deep(.markdown-content h2) {
  font-size: 1.875rem;
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid;
  padding-bottom: 0.5rem;
}

:deep(.markdown-content h3) {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

:deep(.markdown-content h4) {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
}

:deep(.markdown-content p) {
  margin-bottom: 1rem;
}

:deep(.markdown-content ul),
:deep(.markdown-content ol) {
  margin-bottom: 1rem;
  padding-left: 2rem;
}

:deep(.markdown-content ol) {
  list-style-type: decimal;
}

:deep(.markdown-content ul) {
  list-style-type: disc;
}

:deep(.markdown-content li) {
  margin-bottom: 0.5rem;
}

:deep(.markdown-content code) {
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
}

:deep(.markdown-content pre) {
  border: 1px solid;
  border-radius: 0.5rem;
  padding: 1rem;
  overflow-x: auto;
  margin-bottom: 1rem;
}

:deep(.markdown-content pre code) {
  background-color: transparent;
  padding: 0;
}

:deep(.markdown-content blockquote) {
  border-left: 4px solid;
  padding-left: 1rem;
  margin-left: 0;
  margin-bottom: 1rem;
  font-style: italic;
}

:deep(.markdown-content a) {
  text-decoration: none;
}

:deep(.markdown-content a:hover) {
  text-decoration: underline;
}

:deep(.markdown-content table) {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

:deep(.markdown-content th),
:deep(.markdown-content td) {
  border: 1px solid;
  padding: 0.75rem;
  text-align: left;
}

:deep(.markdown-content th) {
  font-weight: 600;
}

:deep(.markdown-content hr) {
  border: none;
  border-top: 1px solid;
  margin: 2rem 0;
}

:deep(.markdown-content img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
}

.markdown-content-card {
  max-width: 900px;
  margin: 0 auto;
}
</style>
